import { useMemo } from 'react';
import {
  parseSummary,
  renderSummaryNodes,
} from '@/features/share/lib/summaryRenderer';

interface Props {
  text: string;
  recordCount: number;
  createdAt?: string | null;
  onCitationClick: (eventId: string) => void;
}

function formatSummaryDate(iso: string): string {
  const d = new Date(iso);
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

export function AiSummaryCard({ text, recordCount, createdAt, onCitationClick }: Props) {
  const paragraphs = useMemo(() => parseSummary(text), [text]);

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center gap-2.5 px-5 pt-4 pb-3 border-b border-border bg-accent-soft/40">
        <span
          aria-hidden="true"
          className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent text-white"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
          >
            <path d="m12 3 2.5 5.5L20 11l-5.5 2.5L12 19l-2.5-5.5L4 11l5.5-2.5L12 3z" />
          </svg>
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-ink">AI Summary</p>
          <p className="text-[11px] text-ink-muted">Generated by Claude Opus 4.7</p>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-accent font-semibold px-2 py-0.5 rounded-full bg-white border border-accent/30">
          Opus 4.7
        </span>
      </div>
      <div className="px-5 py-5 space-y-4">
        {paragraphs.length > 1 ? (
          paragraphs.map((nodes, idx) => (
            <div key={idx} className="flex gap-3">
              <span
                aria-hidden="true"
                className="shrink-0 mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-evt-rx-soft text-evt-rx-ink text-[11px] font-bold tabular-nums"
              >
                {idx + 1}
              </span>
              <p className="text-[15px] leading-relaxed text-ink">
                {renderSummaryNodes(nodes, onCitationClick)}
              </p>
            </div>
          ))
        ) : paragraphs.length === 1 ? (
          <p className="text-[15px] leading-relaxed text-ink">
            {renderSummaryNodes(paragraphs[0], onCitationClick)}
          </p>
        ) : (
          <p className="text-[15px] leading-relaxed text-ink whitespace-pre-line">
            {text}
          </p>
        )}
      </div>
      <div className="px-5 pb-4 border-t border-border pt-3 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-ink-muted italic">
          AI-generated from {recordCount}{' '}
          {recordCount === 1 ? 'record' : 'records'}. Tap a number to see the
          source record.
        </p>
        {createdAt && (
          <p className="text-[11px] text-ink-subtle shrink-0">
            {formatSummaryDate(createdAt)}
          </p>
        )}
      </div>
    </div>
  );
}
